import {
  Container,
  Sprite,
  Texture,
  filters,
  Graphics,
  InteractionEvent,
  WRAP_MODES,
  Ticker,
} from "pixi.js";
import { singleton } from "tsyringe";
import { PixiApp } from "../pixi-app";
import { GlowFilter, TiltShiftAxisFilter } from "pixi-filters";
import gsap from "gsap";
import { EventBus } from "../event-bus";
import { MAIN_COLOR } from "../config/colors";
import { scaleSprite } from "../utils/scale-sprite";
import { AppState } from "../app-state";

interface IButton {
  container: Container;
  alphaFilter: any;
  unmount: () => void;
  enableInteraction: () => void;
  disableInteraction: () => void;
}

const BUTTONS_PAD = 15;
const SCREEN_PAD = 20;
const BTN_SCALE = 0.5;
const THUMB_SCALE = 0.6;
const THUMB_CURSOR_OFFSET = 3;
const DISP_SCALE = 20;
const DISP_SPEED = 0.25;
const DISP_PAD = 25;
const BTN_GLOW_STRENGTH_ACTIVE = 5;
const THUMB_ACTIVE_ALPHA = 1;
const BTN_GLOW_STRENGTH_REST = 3;
class Button implements IButton {
  glow: GlowFilter;
  sprite: Sprite;
  icon: Sprite;
  container: Container;
  alphaFilter: any;
  private glowTween: gsap.core.Tween;
  constructor(
    texture: Texture,
    btnTexture: Texture,
    private eventBus: EventBus
  ) {
    const container = new Container();
    this.createGlow();
    this.createAlphaFilter();
    const btn = new Sprite(btnTexture);
    const icon = new Sprite(texture);
    container.addChild(btn);
    container.addChild(icon);
    container.scale.set(BTN_SCALE, BTN_SCALE);
    container.interactive = true;
    container.cursor = "pointer";
    container.filters = [this.alphaFilter];
    btn.filters = [this.glow];
    this.sprite = btn;
    this.icon = icon;
    this.container = container;
    this.setupEvents();
  }
  unmount() {
    this.unsubscribeEvents();
    this.glowTween?.kill();
    this.container.parent.removeChild(this.container);
    this.container.destroy({ children: true });
    this.glow.destroy();
    // this.alphaFilter.destroy();
  }
  enableInteraction() {
    this.container.interactive = true;
  }
  disableInteraction() {
    this.container.interactive = false;
  }
  private setupEvents() {
    this.container
      .on("mouseover", this.onMouseOver)
      .on("mouseout", this.onMouseOut);
  }
  private unsubscribeEvents() {
    this.container
      .off("mouseover", this.onMouseOver)
      .off("mouseout", this.onMouseOut);
  }
  private onMouseOver = () => {
    this.eventBus.emit("navBtn/pointerOver");
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: BTN_GLOW_STRENGTH_ACTIVE,
      innerStrength: BTN_GLOW_STRENGTH_ACTIVE,
    });
  };
  private onMouseOut = () => {
    this.eventBus.emit("navBtn/pointerOut");
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: BTN_GLOW_STRENGTH_REST,
      innerStrength: BTN_GLOW_STRENGTH_REST,
    });
  };
  private createAlphaFilter() {
    const alpha = new filters.AlphaFilter(0);
    alpha.padding = 10;
    this.alphaFilter = alpha;
  }
  private createGlow() {
    const glow = new GlowFilter({
      color: MAIN_COLOR,
      knockout: true,
      quality: 1,
      innerStrength: BTN_GLOW_STRENGTH_REST,
      outerStrength: BTN_GLOW_STRENGTH_REST,
    });
    this.glow = glow;
  }
}

class Thumb {
  container: Container;
  private sprite: Sprite;
  private alphaFilter: any;
  private dispSprite: Sprite;
  private dispFilter: any;
  private alphaTween: gsap.core.Tween;
  constructor(private texture: Texture, private dispTexture: Texture) {}
  mount(root: Container) {
    const sprite = new Sprite(this.texture);
    const container = new Container();
    container.interactive = false;
    container.interactiveChildren = false;
    this.container = container;
    container.addChild(sprite);
    root.addChild(container);
    container.scale.set(THUMB_SCALE, THUMB_SCALE);
    this.createDisplacementFilter(this.dispTexture);
    this.createAlphaFilter();
    Ticker.shared.add(this.onTick);
    container.filters = [this.alphaFilter, this.dispFilter];
  }
  unmount() {
    Ticker.shared.remove(this.onTick);
    this.alphaTween?.kill();
    this.container.parent?.removeChild(this.container);
    this.container.destroy({ children: true });
    this.alphaFilter.destroy();
    this.dispFilter.destroy();
  }
  enter(root: Container) {
    this.mount(root);
    this.alphaTween?.kill();
    this.alphaTween = gsap.to(this.alphaFilter, {
      duration: 1.25,
      alpha: THUMB_ACTIVE_ALPHA,
    });
  }
  exit() {
    this.alphaTween?.kill();
    this.alphaTween = gsap.to(this.alphaFilter, {
      alpha: 0,
      duration: 0.2,
      onComplete: () => {
        this.unmount();
      },
    });
  }
  private onTick = () => {
    this.dispSprite.x += DISP_SPEED;
    this.dispSprite.y += DISP_SPEED;
  };
  private createAlphaFilter() {
    const alpha = new filters.AlphaFilter(0);
    alpha.padding = 10;
    this.alphaFilter = alpha;
  }
  private createDisplacementFilter(dispTexture: Texture) {
    const sprite = new Sprite(dispTexture);
    const { container } = this;
    container.addChild(sprite);
    sprite.texture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    const filter = new filters.DisplacementFilter(sprite);
    this.dispFilter = filter;
    this.dispSprite = sprite;
    filter.padding = DISP_PAD;
    filter.scale.set(DISP_SCALE, DISP_SCALE);
  }
}
class PaintingButton implements IButton {
  container: Container;
  alphaFilter: any;
  private thumb: Thumb;
  painting: Sprite;
  constructor(
    private texture: Texture,
    private btnTexture: Texture,
    private dispTexture: Texture
  ) {
    const container = new Container();
    this.container = container;
    this.createAlphaFilter();
    const mask = new Sprite(btnTexture);
    mask.scale.set(BTN_SCALE, BTN_SCALE);
    const painting = new Sprite(texture);
    this.painting = painting;
    const { pos, scale } = scaleSprite(painting, mask.width, mask.height);
    painting.position.set(pos.x, pos.y);
    painting.scale.set(scale, scale);
    painting.mask = mask;
    painting.interactive = true;
    painting.cursor = "pointer";
    this.painting = painting;
    container.filters = [this.alphaFilter];
    container.addChild(mask);
    container.addChild(painting);
    this.setupEvents();
  }
  unmount() {
    this.unsubscribeEvents();
    this.thumb?.unmount();
    this.container.parent.removeChild(this.container);
    this.alphaFilter.destroy();
  }
  enableInteraction() {
    this.painting.interactive = true;
  }
  disableInteraction() {
    this.painting.interactive = false;
  }
  private setupEvents() {
    this.painting.on("mouseover", this.onMouseOver);
    this.painting.on("mouseout", this.onMouseOut);
  }
  private unsubscribeEvents() {
    this.painting.off("mouseover", this.onMouseOver);
    this.painting.off("mouseout", this.onMouseOut);
    this.painting.off("mousemove", this.onMouseMove);
  }
  private onMouseOver = (e: InteractionEvent) => {
    const { container, painting, texture } = this;
    const point = container.toLocal(e.data.global);
    const thumb = new Thumb(texture, this.dispTexture);
    thumb.enter(container);
    thumb.container.x = point.x - thumb.container.width - THUMB_CURSOR_OFFSET;
    thumb.container.y = point.y - thumb.container.height - THUMB_CURSOR_OFFSET;
    painting.on("mousemove", this.onMouseMove);
    this.thumb = thumb;
  };
  private onMouseOut = () => {
    this.painting.off("mousemove", this.onMouseMove);
    this.thumb?.exit();
  };
  private onMouseMove = (e: InteractionEvent) => {
    const point = this.container.toLocal(e.data.global);
    const { thumb } = this;
    thumb.container.x = point.x - thumb.container.width - THUMB_CURSOR_OFFSET;
    thumb.container.y = point.y - thumb.container.height - THUMB_CURSOR_OFFSET;
  };
  private createAlphaFilter() {
    const alpha = new filters.AlphaFilter(0);
    alpha.padding = 10;
    this.alphaFilter = alpha;
  }
}

@singleton()
export class PaintingNavComponent {
  private container: Container;
  private buttons: IButton[] = [];
  private isMounted = false;
  private tween: gsap.core.Tween;
  constructor(
    private pixiApp: PixiApp,
    private eventBus: EventBus,
    private appState: AppState
  ) {}
  mount() {
    this.container = new Container();
    this.createButtons();
    this.setupEvents();
    this.setButtonsPosition();
    this.setContainerPosition();
    this.pixiApp.stage.addChild(this.container);
    this.isMounted = true;
  }
  unmount() {
    this.tween?.kill();
    this.buttons.forEach((button) => button.unmount());
    this.container.parent.removeChild(this.container);
    this.container.destroy({ children: true });
    this.unsubscribeEvents();
    this.isMounted = false;
  }
  async enter() {
    this.mount();
    this.enableInteraction();
    await this.playEnterAnimation();
  }
  async exit() {
    this.disableInteraction();
    await this.playExitAnimation();
    this.unmount();
  }
  private playEnterAnimation() {
    this.tween?.kill();
    return new Promise((res) => {
      this.tween = gsap.to(
        this.buttons.map((button) => button.alphaFilter).reverse(),
        {
          alpha: 1,
          stagger: 0.05,
          duration: 1.5,
          onComplete: res,
        }
      );
    });
  }
  private playExitAnimation() {
    this.tween?.kill();
    return new Promise((res) => {
      this.tween = gsap.to(
        this.buttons.map((button) => button.alphaFilter),
        {
          alpha: 0,
          stagger: 0.05,
          duration: 0.275,
          onComplete: res,
        }
      );
    });
  }
  disableInteraction() {
    this.buttons.forEach((button) => {
      button.disableInteraction();
    });
  }
  enableInteraction() {
    this.buttons.forEach((button) => {
      button.enableInteraction();
    });
  }
  private createButtons() {
    const { pixiApp } = this;
    const { resources } = pixiApp.loader;
    const createBtn = (texture: Texture) =>
      new Button(texture, resources.circle.texture!, this.eventBus);
    const createPaintingBtn = (texture: Texture) =>
      new PaintingButton(
        texture,
        resources.circle.texture!,
        this.pixiApp.loader.resources.textDisp.texture!
      );
    const homeBtn = createBtn(resources.homeIcon.texture!);
    homeBtn.container.on("pointerup", () => {
      this.eventBus.emit("navBtn/home");
    });
    const nextPaintingBtn = createPaintingBtn(
      resources[this.appState.nextPainting.key + "thumb"].texture!
    );
    nextPaintingBtn.painting.on("pointerup", () => {
      this.eventBus.emit("navBtn/next");
    });
    const prevPaintingBtn = createPaintingBtn(
      resources[this.appState.prevPainting.key + "thumb"].texture!
    );
    prevPaintingBtn.painting.on("pointerup", () => {
      this.eventBus.emit("navBtn/prev");
    });
    const buttons = [prevPaintingBtn, nextPaintingBtn, homeBtn];
    this.buttons = buttons;
    buttons.forEach((btn) => {
      this.container.addChild(btn.container);
    });
  }
  private setContainerPosition() {
    this.container.position.set(
      document.documentElement.clientWidth -
        this.buttons[0].container.height -
        SCREEN_PAD,
      document.documentElement.clientHeight - this.container.height - SCREEN_PAD
    );
  }
  private setButtonsPosition() {
    this.buttons.reduce((offset, btn) => {
      btn.container.y = offset;
      offset += btn.container.height + BUTTONS_PAD;
      return offset;
    }, 0);
  }
  private onResize = () => {
    if (this.isMounted) {
      this.setContainerPosition();
    }
  };
  private setupEvents() {
    this.eventBus.on("resize", this.onResize);
  }
  private unsubscribeEvents() {
    this.eventBus.off("resize", this.onResize);
    this.eventBus.on("resize", this.onResize);
  }
}
